import { useTranslation } from 'react-i18next';
import { LogOut, User } from 'lucide-react';
import { Button, PageContainer, TypographyPageTitle, TypographySmall, TypographyMuted } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { SettingsMenu } from './settings-menu';
import { useAuth } from '@entities/auth';
import { useBalance } from '@features/balance';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';

interface PageHeaderProps {
  title: string;
  className?: string;
}

export function PageHeader({ title, className }: PageHeaderProps) {
  const { t } = useTranslation('cabinet');
  const { user, logout } = useAuth();
  const { balance } = useBalance();

  return (
    <header className={cn('sticky top-0 z-10 shrink-0 bg-background pt-[var(--layout-header-padding-top,36px)]', className)}>
      <PageContainer>
        <div className="flex h-12 items-center justify-between gap-4">
          <TypographyPageTitle className="truncate">{title}</TypographyPageTitle>

          <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-3">
            <SettingsMenu />

            <div className="flex min-w-0 items-center gap-2 rounded-xl border border-border bg-background px-2 py-1.5 sm:gap-3 sm:px-3 sm:py-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="min-w-0 flex-1 sm:flex-none">
                <TypographySmall className="truncate">{user?.email}</TypographySmall>
                <TypographyMuted className="hidden md:block">
                  {t('balance.available')}: {balance?.availableBalance == null ? t('balance.amountUnknown') : formatLedgerAmount(balance.availableBalance)}
                </TypographyMuted>
              </div>

              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void logout()}
                className="shrink-0 text-destructive sm:hidden"
                aria-label={t('header.logout')}
              >
                <LogOut className="h-4 w-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => void logout()}
                className="hidden shrink-0 text-destructive sm:inline-flex"
              >
                {t('header.logout')}
              </Button>
            </div>
          </div>
        </div>
      </PageContainer>
    </header>
  );
}
